<template>
  <div class="card import-category-card mb-3" :class="{ 'is-collapsed': !isExpanded }">
    <header
      class="card-header"
      role="button"
      tabindex="0"
      :aria-expanded="isExpanded"
      @click="toggle"
      @keydown.enter="toggle"
      @keydown.space.prevent="toggle"
    >
      <div class="card-header-title is-flex is-align-items-center">
        <label class="checkbox checkbox-hit-area" @click.stop>
          <input v-model="isSelected" type="checkbox" :disabled="disabled" />
        </label>
        <span class="category-name">{{ categoryLabel }}</span>
        <span v-if="itemCount > 0" class="tag is-light ml-2">
          {{ itemCount }} {{ itemCount === 1 ? itemUnit : itemUnit + "s" }}
        </span>
        <span v-if="willOverwrite && isSelected" class="overwrite-notice ml-2">
          Overwrites current
        </span>
      </div>
      <button class="card-header-icon" aria-label="toggle details">
        <span class="icon chevron-icon">
          <i class="fas fa-angle-down" aria-hidden="true"></i>
        </span>
      </button>
    </header>

    <div v-show="isExpanded" class="card-content">
      <!-- Lines table (for nutrients) -->
      <table
        v-if="category.lines && category.lines.length > 0"
        class="table is-fullwidth is-narrow is-striped"
      >
        <thead>
          <tr>
            <th>Name</th>
            <th class="has-text-right">Amount</th>
            <th>Unit</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(line, idx) in category.lines" :key="idx">
            <td :style="{ paddingLeft: (line.indent || 0) * 1.5 + 0.75 + 'em' }">
              {{ line.name }}
            </td>
            <td class="has-text-right">{{ formatAmount(line.amount) }}</td>
            <td>{{ line.unit }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Serving info (key-value) -->
      <div v-else-if="category.data && categoryKey === 'serving_info'" class="content">
        <p><strong>Serving Size:</strong> {{ category.data.serving_size_text || "(not set)" }}</p>
        <p>
          <strong>Servings Per Container:</strong>
          {{ category.data.servings_per_container || "(not set)" }}
        </p>
      </div>

      <!-- Generic text data (Other ingredients, Allergens, etc.) -->
      <div
        v-else-if="
          category.data &&
          ['other_ingredients', 'allergens', 'facility_allergens', 'business_info'].includes(
            categoryKey
          )
        "
        class="content"
      >
        <p style="white-space: pre-wrap">{{ category.data }}</p>
      </div>

      <!-- Empty state -->
      <p v-else class="has-text-grey-light is-italic">No data available</p>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed } from "vue";

  const props = defineProps({
    category: { type: Object, required: true },
    categoryKey: { type: String, required: true },
    modelValue: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    willOverwrite: { type: Boolean, default: false },
  });

  const emit = defineEmits(["update:modelValue"]);

  const isExpanded = ref(false);
  const isSelected = computed({
    get: () => props.modelValue,
    set: (val) => emit("update:modelValue", val),
  });

  const itemCount = computed(() => {
    if (props.category.count !== undefined) return props.category.count;
    if (props.category.lines) return props.category.lines.length;
    if (props.category.data) return 1;
    return 0;
  });

  const toggle = () => {
    isExpanded.value = !isExpanded.value;
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "-";
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  const itemUnit = computed(() => {
    if (["macronutrients", "micronutrients", "non_dv"].includes(props.categoryKey)) {
      return "line";
    }
    return "item";
  });

  const categoryLabel = computed(() => {
    if (props.categoryKey === "non_dv") {
      return "Ingredients without DVs";
    }
    return props.category.label;
  });
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_editor";
  @import "~/label/css/labels/supplements/supplement_mixins";

  .import-category-card {
    border: 1px solid $border-standard;
    border-radius: 8px;
    background: white;

    @include card-shadow;

    .card-header {
      cursor: pointer;
      box-shadow: none;
      border-bottom: 1px solid $border-standard;

      &:hover {
        background-color: $bg-light-gray;
      }

      &:focus {
        outline: 2px solid $focus-outline-color;
        outline-offset: -2px;
      }
    }

    .table {
      font-size: 0.875rem;
    }
  }

  .category-name {
    font-weight: 500;
  }

  .checkbox-hit-area {
    padding: 1rem;
    margin: -1rem;
    margin-right: 0.25rem;
  }

  .overwrite-notice {
    font-size: 0.75rem;
    font-weight: 600;
    font-style: italic;
    color: #d97706;
    background-color: #fff7ed;
    padding: 0.1em 0.4em;
    border-radius: 3px;
  }

  @include chevron-rotation(".import-category-card", ".card-header");
</style>
